import React from 'react';

interface MacysConfigProps {
  config: {
    stockLevel: number;
    batchSize: number;
    requestsPerSecond: number;
    handlingTimeOmd: number;
    providerSpecificHandlingTime: number;
    updateFlagValue: number;
    priceMarkupPercentage?: number;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Configuration component for Macys provider
 */
const MacysConfig: React.FC<MacysConfigProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Stock Level (Macys)
        </label>
        <input
          id="stockLevel"
          type="number"
          name="stockLevel"
          value={config.stockLevel || ''}
          onChange={onChange}
          className="input"
          aria-label="Stock level for Macys"
        />
        <p className="text-sm text-gray-500 mt-1">
          Maximum stock level to be considered for Macys products.
        </p>
      </div>
      
      <div>
        <label htmlFor="handlingTimeOmd" className="block text-sm font-medium text-gray-700 mb-1">
          OMD Handling Time (days)
        </label>
        <input
          id="handlingTimeOmd"
          type="number"
          name="handlingTimeOmd"
          value={config.handlingTimeOmd || ''}
          onChange={onChange}
          className="input"
          aria-label="OMD handling time in days"
        />
        <p className="text-sm text-gray-500 mt-1">
          OMD handling time for Macys products.
        </p>
      </div>
      
      <div>
        <label htmlFor="macysHandlingTime" className="block text-sm font-medium text-gray-700 mb-1">
          Macys Handling Time (days)
        </label>
        <input
          id="macysHandlingTime"
          type="number"
          name="providerSpecificHandlingTime"
          value={config.providerSpecificHandlingTime || ''}
          onChange={onChange}
          className="input"
          aria-label="Macys handling time in days"
        />
        <p className="text-sm text-gray-500 mt-1">
          Specific handling time for Macys products. The API may override this with handling_days field.
        </p>
      </div>
      
      <div>
        <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
          Batch Size
        </label>
        <input
          id="batchSize"
          type="number"
          name="batchSize"
          value={config.batchSize || ''}
          onChange={onChange}
          className="input"
          aria-label="Batch size"
        />
        <p className="text-sm text-gray-500 mt-1">
          Number of products to be processed in each batch during Amazon submission.
        </p>
      </div>
      
      <div>
        <label htmlFor="requestsPerSecond" className="block text-sm font-medium text-gray-700 mb-1">
          Requests Per Second
        </label>
        <input
          id="requestsPerSecond"
          type="number"
          name="requestsPerSecond"
          value={config.requestsPerSecond || ''}
          onChange={onChange}
          className="input"
          aria-label="Requests per second"
        />
        <p className="text-sm text-gray-500 mt-1">
          API request rate limit for Macys (to avoid blocking).
        </p>
      </div>
      
      <div>
        <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
          Update Flag Code (Macys ID)
        </label>
        <input
          id="updateFlagValue"
          type="number"
          name="updateFlagValue"
          value={config.updateFlagValue || ''}
          onChange={onChange}
          className="input"
          aria-label="Update flag code"
        />
        <p className="text-sm text-gray-500 mt-1">
          Unique identifier used to mark products updated by Macys. Use a unique value for each store.
        </p>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
        <h3 className="text-blue-800 font-medium mb-2">Macys Information</h3>
        <p className="text-sm text-blue-700">
          Macys provides product availability and pricing information via Oalizo API.
          The system uses a composite identifier (supplier_sku:supplier_upc) for API calls.
          The API returns handling_days which may override the configured handling time.
          Make sure both SKU and UPC are present in the database for successful syncs.
        </p>
      </div>

      {/* Phase 2.1 - Price Markup Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          💰 Phase 2.1 - Price Markup
        </h3>
        
        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            value={config.priceMarkupPercentage || ''}
            onChange={onChange}
            className="input"
            step="0.1"
            min="0"
            aria-label="Price markup percentage"
          />
          <p className="text-sm text-gray-500 mt-1">
            Markup percentage applied when calculating Amazon price.
          </p>
          <div className="mt-2 p-3 bg-white rounded border border-purple-200">
            <p className="text-xs text-gray-600 mb-1">
              📊 <strong>Formula:</strong> Amazon Price = (Supplier_price + shipping) × (1 + markup% / 100)
            </p>
            <p className="text-xs text-green-600">
              📝 <strong>Example:</strong> Product $10 + Shipping $5 with 15% markup = $17.25
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacysConfig;
